import {Fragment, FunctionalComponent, h, JSX} from "@stencil/core";
import type {Notification} from "../stores/notificationsStore";
import {i18nProvider} from "@trumpf/ux.i18n";

const {t} = i18nProvider;

const NotificationItem: FunctionalComponent<Notification> = ({severity, sender, creationDate, creationTime, remainingTime, subject, message}) => (
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	<article class={"notification-item"}>
		<header>
			<ux-icon id="severity" name={`notification-${severity.toLowerCase()}`} />
			<span id="sender" class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
				{sender}
			</span>
			<div id="times">
				<time id="creation-date" class="ux-text-tiny">
					{creationDate}
				</time>
				<time id="creation-time" class="ux-text-tiny">
					{creationTime}
				</time>
				<ux-icon title={t("center:ttlTooltip")} name="clock" />
				<span id="ttl" class="ux-text-tiny">
					{remainingTime}
				</span>
			</div>
		</header>
		<p id="subject" class="ux-text-tiny ux-text-tiny--bold">
			{subject}
		</p>
		<p id="message" class="ux-text-tiny" innerHTML={message} />
	</article>
);

const Separator: FunctionalComponent<{caption?: Notification["dateGroup"]}> = ({caption}) => {
	const label = caption === undefined ? "" : t(`center:${caption}Label`);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return <hr class="separator ux-text-tiny ux-u-text-uppercase" aria-label={label} />;
};

export const Header: FunctionalComponent<{settingsURL: string; isLoading: boolean; hasFailed: boolean; count: number}> = ({
	settingsURL,
	isLoading,
	hasFailed,
	count,
}) => {
	let info: JSX.Element | null;

	switch (true) {
		case hasFailed:
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			info = <p class="ux-text-tiny ux-color-error">{t("center:genericError")}</p>;
			break;
		case !isLoading && count === 0:
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			info = <p class="ux-text-tiny ux-color-disabled">{t("center:noMessages")}</p>;
			break;
		default:
			info = null;
			break;
	}

	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return (
		<header>
			{isLoading && <ux-loader variant="horizontal" />}
			{!isLoading && info}
			<a href={settingsURL} target="_blank">
				<ux-icon title={t("center:settingsTooltip")} name="cog" />
			</a>
		</header>
	);
};

/** Renders given notifications within a captioned group */
export const CaptionedGroup: FunctionalComponent<{caption: Notification["dateGroup"]; notifications: Notification[]}> = ({caption, notifications}) => {
	if (notifications.length === 0) return null;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return (
		<Fragment>
			<Separator caption={caption} />
			{notifications.map((notification, index) => (
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return
				<Fragment>
					<NotificationItem {...notification} />
					{index < notifications.length - 1 && <Separator />}
				</Fragment>
			))}
		</Fragment>
	);
};
